/**
 * The people of one company: invite, change roles, remove and restore access,
 * sign out everywhere, reset passkeys, and the recent changes.
 *
 * Used by the company's own administrators (Access page) and by EVT platform
 * administrators (a company's page under Tenants). The server decides every
 * permission; this component only hides what it would refuse — the passkey
 * reset of someone who is also in another company, for a company admin — and
 * names the reason when the server refuses anyway (`describeApiError`).
 *
 * Destructive actions confirm inline, in the row they affect, rather than in a
 * browser dialog: the person reading the confirmation sees exactly whose access
 * changes.
 */
import { Badge, Icon, Notice, Spinner } from '@evt/flow-stream-ui'
import type { AccessAuditEntry, AccessMember, AccessTenant, InviteResult, TenantRole } from '@portal/contracts'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useMemo, useState } from 'react'
import {
  getAccessAudit,
  inviteMembers,
  listMembers,
  resendInvite,
  resetMemberPasskeys,
  signOutMember,
  updateMember
} from '../../api/access'
import { useSession } from '../../auth/useSession'
import { describeApiError, type Messages, useI18n } from '../../i18n'

export const accessKey = (tenantId: string, part: 'members' | 'audit' | 'tenant') =>
  ['portal', 'access', tenantId, part] as const

/**
 * What a change of the company's flows did, in words: "added Smith UiPath; removed FR Docs".
 * The detail is the audit row's, written by the BFF; read defensively, since a row outlives
 * the code that wrote it. Exported for tests.
 */
export function flowsChangeLine(t: Messages, detail: Record<string, unknown> | undefined): string {
  const names = (v: unknown) =>
    Array.isArray(v)
      ? v
          .map((x) => (x && typeof x === 'object' ? (x as { name?: unknown; flowId?: unknown }) : {}))
          .map((x) => (typeof x.name === 'string' ? x.name : typeof x.flowId === 'string' ? x.flowId : ''))
          .filter(Boolean)
      : []
  const parts: string[] = []
  const mode = detail?.mode as { to?: unknown } | undefined
  if (mode?.to === 'all') parts.push(t.access.activityFlowsModeAll)
  if (mode?.to === 'selected') parts.push(t.access.activityFlowsModeSelected)
  const added = names(detail?.added)
  const removed = names(detail?.removed)
  if (added.length > 0) parts.push(t.access.activityFlowsAdded(added.join(', ')))
  if (removed.length > 0) parts.push(t.access.activityFlowsRemoved(removed.join(', ')))
  return parts.join('; ')
}

/** Split a pasted list (lines, commas, semicolons, spaces) into addresses. Exported for tests. */
export function parseEmailList(raw: string): string[] {
  return [
    ...new Set(
      raw
        .split(/[\s,;]+/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
    )
  ]
}

/**
 * The addresses a company's allowed domains refuse — the server's rule (exact domain after the @), so the panel can
 * name them instead of answering "an address is outside" for a pasted list of fifty. The server still decides.
 */
export function outsideDomains(emails: string[], domains: string[]): string[] {
  if (domains.length === 0) return []
  return emails.filter((e) => !domains.includes(e.split('@')[1] ?? ''))
}

type Pending = { userId: string; kind: 'revoke' | 'reset' | 'signout' } | null

export function AccessPanel({ tenantId, tenant }: { tenantId: string; tenant: AccessTenant | undefined }) {
  const { t, locale, formatAgo, formatDateTime } = useI18n()
  const { user, platformAdmin } = useSession()
  const qc = useQueryClient()
  const members = useQuery({ queryKey: accessKey(tenantId, 'members'), queryFn: () => listMembers(tenantId) })
  const audit = useQuery({ queryKey: accessKey(tenantId, 'audit'), queryFn: () => getAccessAudit(tenantId) })

  const [emails, setEmails] = useState('')
  const [role, setRole] = useState<TenantRole>('member')
  const [sendEmail, setSendEmail] = useState(true)
  const [mailLocale, setMailLocale] = useState<'en' | 'pt-BR'>(locale === 'en' ? 'en' : 'pt-BR')
  const [inviting, setInviting] = useState(false)
  const [inviteResults, setInviteResults] = useState<InviteResult[] | null>(null)
  const [inviteError, setInviteError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [showRevoked, setShowRevoked] = useState(false)
  const [pending, setPending] = useState<Pending>(null)
  const [busyRow, setBusyRow] = useState<string | null>(null)
  const [rowMessage, setRowMessage] = useState<{ userId: string; kind: 'ok' | 'error'; text: string } | null>(null)

  const domains = tenant?.settings.allowedEmailDomains ?? []

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: accessKey(tenantId, 'members') }),
      qc.invalidateQueries({ queryKey: accessKey(tenantId, 'audit') }),
      qc.invalidateQueries({ queryKey: accessKey(tenantId, 'tenant') }),
      qc.invalidateQueries({ queryKey: ['portal', 'platform'] })
    ])
  }

  const onInvite = async (e: FormEvent) => {
    e.preventDefault()
    const list = parseEmailList(emails)
    if (list.length === 0) return
    const outside = outsideDomains(list, domains)
    if (outside.length > 0) {
      setInviteResults(null)
      setInviteError(t.access.inviteOutside(outside, domains.join(', ')))
      return
    }
    setInviting(true)
    setInviteError(null)
    setInviteResults(null)
    try {
      const results = await inviteMembers(tenantId, { emails: list, role, sendEmail, locale: mailLocale })
      setInviteResults(results)
      setEmails('')
      await refresh()
    } catch (err) {
      setInviteError(describeApiError(t, err))
    } finally {
      setInviting(false)
    }
  }

  const act = async (member: AccessMember, run: () => Promise<string | undefined>) => {
    setBusyRow(member.userId)
    setRowMessage(null)
    try {
      const done = await run()
      if (done) setRowMessage({ userId: member.userId, kind: 'ok', text: done })
      setPending(null)
      await refresh()
    } catch (err) {
      setRowMessage({ userId: member.userId, kind: 'error', text: describeApiError(t, err) })
    } finally {
      setBusyRow(null)
    }
  }

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (members.data ?? [])
      .filter((m) => showRevoked || m.status !== 'revoked')
      .filter((m) => !q || m.email.includes(q) || m.displayName.toLowerCase().includes(q))
  }, [members.data, search, showRevoked])

  return (
    <div className="access-panel">
      <section className="card">
        <div className="card-head">
          <div className="card-title">{t.access.inviteTitle}</div>
        </div>
        <form className="card-pad access-invite" onSubmit={(e) => void onInvite(e)}>
          <p className="muted access-help">{t.access.inviteHelp}</p>
          {domains.length > 0 && <p className="muted access-help">{t.access.policyDomains(domains.join(', '))}</p>}
          <label className="field">
            <span className="field-label">{t.access.inviteEmails}</span>
            <textarea
              className="textarea"
              rows={3}
              value={emails}
              placeholder={t.access.inviteEmailsPlaceholder}
              onChange={(e) => setEmails(e.target.value)}
              aria-label={t.access.inviteEmails}
            />
          </label>
          <div className="access-invite-row">
            <label className="field">
              <span className="field-label">{t.access.inviteRole}</span>
              <select className="select" value={role} onChange={(e) => setRole(e.target.value as TenantRole)}>
                <option value="member">{t.access.roleMember}</option>
                <option value="admin">{t.access.roleAdmin}</option>
              </select>
            </label>
            <label className="field">
              <span className="field-label">{t.access.inviteLanguage}</span>
              <select
                className="select"
                value={mailLocale}
                onChange={(e) => setMailLocale(e.target.value as 'en' | 'pt-BR')}
              >
                <option value="pt-BR">{t.common.languageNamePt}</option>
                <option value="en">{t.common.languageNameEn}</option>
              </select>
            </label>
            <label className="access-check">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
              <span>{t.access.inviteSendEmail}</span>
            </label>
            <button type="submit" className="btn btn-primary btn-sm" disabled={inviting || !emails.trim()}>
              {inviting ? <Spinner size={13} /> : <Icon name="plus" size={13} />} {t.access.inviteSubmit}
            </button>
          </div>
          <p className="muted access-help">{t.access.roleHelp}</p>
          {inviteError && <Notice kind="danger">{inviteError}</Notice>}
          {inviteResults && (
            <ul className="access-results">
              {inviteResults.map((r) => (
                <li key={r.email}>
                  <strong>{r.email}</strong> —{' '}
                  {r.outcome === 'invited'
                    ? t.access.inviteOutcomeInvited
                    : r.outcome === 'reactivated'
                      ? t.access.inviteOutcomeReactivated
                      : t.access.inviteOutcomeAlready}
                  {r.emailed && <span className="muted"> · {t.access.inviteEmailed}</span>}
                  {r.emailError && <span className="access-warn"> · {t.access.inviteEmailFailed}</span>}
                </li>
              ))}
            </ul>
          )}
        </form>
      </section>

      <section className="card">
        <div className="card-head access-members-head">
          <div className="card-title">{t.access.membersTitle}</div>
          <div className="access-filters">
            <input
              className="input"
              type="search"
              value={search}
              placeholder={t.access.searchPlaceholder}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={t.access.searchPlaceholder}
            />
            <label className="access-check">
              <input type="checkbox" checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} />
              <span>{t.access.showRevoked}</span>
            </label>
          </div>
        </div>
        {members.isLoading ? (
          <div className="card-pad">
            <Spinner size={16} />
          </div>
        ) : members.error ? (
          <div className="card-pad">
            <Notice kind="danger">{describeApiError(t, members.error)}</Notice>
          </div>
        ) : visible.length === 0 ? (
          <p className="card-pad muted">{t.access.membersEmpty}</p>
        ) : (
          <div className="access-table-wrap">
            <table className="tbl access-table">
              <thead>
                <tr>
                  <th>{t.access.colPerson}</th>
                  <th>{t.access.colRole}</th>
                  <th>{t.access.colStatus}</th>
                  <th>{t.access.colSignIn}</th>
                  <th>{t.access.colActions}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((m) => {
                  const isMe = m.userId === user?.id
                  const busy = busyRow === m.userId
                  const canReset = m.passkeys > 0 && (platformAdmin || m.otherTenants === 0)
                  const confirming = pending?.userId === m.userId ? pending.kind : null
                  return (
                    <tr key={m.userId} className={m.status === 'revoked' ? 'is-revoked' : undefined}>
                      <td>
                        <div className="access-person">
                          <span>{m.email}</span>
                          {isMe && <span className="chip">{t.access.you}</span>}
                        </div>
                        <div className="muted access-sub">
                          {m.source === 'email-domain' && t.access.viaDomain}
                          {(m.source === 'sso-provider' || m.source === 'entra-tenant') && t.access.viaSso}
                          {m.otherTenants > 0 && ` ${t.access.otherTenants(m.otherTenants)}`}
                        </div>
                      </td>
                      <td>{m.role === 'admin' ? t.access.roleAdmin : t.access.roleMember}</td>
                      <td>
                        <Badge
                          kind={m.status === 'active' ? 'success' : m.status === 'invited' ? 'warning' : 'disabled'}
                        >
                          {m.status === 'active'
                            ? t.access.statusActive
                            : m.status === 'invited'
                              ? t.access.statusInvited
                              : t.access.statusRevoked}
                        </Badge>
                      </td>
                      <td className="access-sub">
                        <div>
                          {m.lastLoginAt ? t.access.lastSignIn(formatAgo(m.lastLoginAt)) : t.access.neverSignedIn}
                        </div>
                        <div className="muted">
                          {t.access.passkeys(m.passkeys)}
                          {m.sso && ` · ${t.access.sso}`}
                        </div>
                      </td>
                      <td>
                        {confirming ? (
                          <div className="access-confirm">
                            <span>
                              {confirming === 'revoke'
                                ? t.access.confirmRevoke(m.email)
                                : confirming === 'reset'
                                  ? t.access.confirmResetPasskeys(m.email)
                                  : t.access.confirmSignOut(m.email)}
                            </span>
                            <div className="access-actions">
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                disabled={busy}
                                onClick={() =>
                                  void act(m, async () => {
                                    if (confirming === 'revoke') {
                                      await updateMember(tenantId, m.userId, { status: 'revoked' })
                                      return undefined
                                    }
                                    if (confirming === 'reset') {
                                      const r = await resetMemberPasskeys(tenantId, m.userId)
                                      return t.access.donePasskeysReset(r.removed)
                                    }
                                    await signOutMember(tenantId, m.userId)
                                    return t.access.doneSignedOut
                                  })
                                }
                              >
                                {busy ? <Spinner size={12} /> : null} {t.access.confirm}
                              </button>
                              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPending(null)}>
                                {t.common.cancel}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="access-actions">
                            {m.status === 'revoked' ? (
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                disabled={busy}
                                onClick={() =>
                                  void act(m, async () => {
                                    await updateMember(tenantId, m.userId, { status: 'active' })
                                    return undefined
                                  })
                                }
                              >
                                {t.access.actionReactivate}
                              </button>
                            ) : (
                              <>
                                {!isMe && (
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    disabled={busy}
                                    onClick={() =>
                                      void act(m, async () => {
                                        await updateMember(tenantId, m.userId, {
                                          role: m.role === 'admin' ? 'member' : 'admin'
                                        })
                                        return undefined
                                      })
                                    }
                                  >
                                    {m.role === 'admin' ? t.access.actionMakeMember : t.access.actionMakeAdmin}
                                  </button>
                                )}
                                {m.status === 'invited' && (
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    disabled={busy}
                                    onClick={() =>
                                      void act(m, async () => {
                                        await resendInvite(tenantId, m.userId, mailLocale)
                                        return t.access.doneResent
                                      })
                                    }
                                  >
                                    {t.access.actionResend}
                                  </button>
                                )}
                                {m.status === 'active' && !isMe && (
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    disabled={busy}
                                    onClick={() => setPending({ userId: m.userId, kind: 'signout' })}
                                  >
                                    {t.access.actionSignOut}
                                  </button>
                                )}
                                {canReset && !isMe && (
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    disabled={busy}
                                    onClick={() => setPending({ userId: m.userId, kind: 'reset' })}
                                  >
                                    {t.access.actionResetPasskeys}
                                  </button>
                                )}
                                {!isMe && (
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-sm access-danger"
                                    disabled={busy}
                                    onClick={() => setPending({ userId: m.userId, kind: 'revoke' })}
                                  >
                                    {t.access.actionRevoke}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )}
                        {rowMessage?.userId === m.userId && (
                          <div className={rowMessage.kind === 'error' ? 'access-warn' : 'muted'}>{rowMessage.text}</div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <div className="card-head">
          <div className="card-title">{t.access.activityTitle}</div>
        </div>
        <div className="card-pad">
          {audit.isLoading ? (
            <Spinner size={14} />
          ) : (audit.data ?? []).length === 0 ? (
            <p className="muted">{t.access.activityEmpty}</p>
          ) : (
            <ul className="access-activity">
              {(audit.data ?? []).map((a: AccessAuditEntry) => (
                <li key={a.id}>
                  <span className="muted">{formatDateTime(a.at)}</span> —{' '}
                  <strong>{t.access.activityBy(a.actorEmail, a.actorIsPlatform)}</strong>{' '}
                  {t.access.actions[a.action] ?? a.action} {a.targetEmail && <strong>{a.targetEmail}</strong>}
                  {a.action === 'member.role_changed' && a.detail?.to ? (
                    <span className="muted">
                      {' '}
                      → {a.detail.to === 'admin' ? t.access.roleAdmin : t.access.roleMember}
                    </span>
                  ) : null}
                  {a.action === 'tenant.flows_changed' && flowsChangeLine(t, a.detail) && (
                    <span className="muted"> — {flowsChangeLine(t, a.detail)}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  )
}
